import { ContainerBloco, TituloBloco } from "../../../Demanda/Common/styles";
import { Container } from "../styles";
import { Clock } from "lucide-react";
import { comum } from "../../../../styles/Themes/light";
import { IItemHistoricoServico } from "../types";
import ItemHistorico from "../ItemHistorico";
import SkeletonItemHistorico from "../ItemHistorico/SkeletonItemHistorico/index";
import HistoricoVazio from "../HistoricoVazio/index";
import HistoricoErro from "../HistoricoErro/index";
import { useEffect, useState, useCallback } from "react";
import api from "../../../../services/api";
import { formatServicoColuna } from "../../../../utils/servicoMapperPlanos";

const JANELA_AGRUPAMENTO_MS = 5000; // 5 segundos

const isServicoEditado = (acao: string): boolean => {
  const a = (acao || '').toLowerCase().trim();
  return a === 'edição' || a.includes('serviço editado');
};

/**
 * Agrupa itens de "Edição" que ocorreram na mesma janela de tempo (mesmo responsável).
 * Comportamento igual ao histórico de planos: múltiplas edições em batch viram um único card.
 */
function agruparHistoricoServico(itens: IItemHistoricoServico[]): IItemHistoricoServico[] {
  const resultado: IItemHistoricoServico[] = [];
  let i = 0;

  while (i < itens.length) {
    const item = itens[i];
    if (!isServicoEditado(item.acao)) {
      resultado.push(item);
      i++;
      continue;
    }

    const grupo: IItemHistoricoServico[] = [item];
    const t0 = new Date(item.criado).getTime();
    const responsavel = item.nome_responsavel || '';

    while (i + 1 < itens.length) {
      const prox = itens[i + 1];
      if (!isServicoEditado(prox.acao) || (prox.nome_responsavel || '') !== responsavel) break;
      const tProx = new Date(prox.criado).getTime();
      if (t0 - tProx > JANELA_AGRUPAMENTO_MS) break;
      grupo.push(prox);
      i++;
    }

    if (grupo.length === 1) {
      resultado.push(item);
    } else {
        resultado.push({
          ...grupo[0],
          id: grupo[0].id,
          payload: {
            servicos: grupo.map((g) => {
              const p = typeof g.payload === 'string' ? JSON.parse(g.payload || '{}') : g.payload || {};
              return {
                id_servico_template: g.id_servico_template,
                nome_servico: formatServicoColuna(g.area_direito, g.servico, g.tipo_audiencia),
                servico: g.servico,
                area_direito: g.area_direito,
                tipo_audiencia: g.tipo_audiencia,
                antes: p?.antes,
                depois: p?.depois,
              };
            }),
          },
        });
    }
    i++;
  }

  return resultado;
}

interface AbaHistoricoServicoProps {
  abaAtiva: string;
  loading?: boolean;
}

export default function AbaHistoricoServico({ abaAtiva, loading: loadingParent }: AbaHistoricoServicoProps) {
  const [historico, setHistorico] = useState<IItemHistoricoServico[]>([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(false);

  const carregaHistorico = useCallback(async () => {
    try {
      setLoading(true);
      setErro(false);
      const response = await api.get(`/servico-template/historico`);

      if (response.data.status === 'error') {
        throw new Error(response.data.message || 'Erro ao carregar histórico');
      }

      if (response.data.status === 'success') {
        const historicoData = response.data.result || [];
        
        const historicoMapeado: IItemHistoricoServico[] = historicoData
          .map((item: any) => ({
            id: item.id || item.id_historico_servico_template,
            id_servico_template: item.id_servico_template || 0,
            criado: item.criado || '',
            acao: item.acao || '',
            acao_original: item.acao || '',
            nome_responsavel: item.nome_responsavel || '',
            tipo_responsavel: item.tipo_responsavel || '',
            dispositivo_tipo: item.dispositivo_tipo || 'desktop',
            payload: item.payload || null,
            servico: item.servico ?? null,
            area_direito: item.area_direito ?? null,
            tipo_audiencia: item.tipo_audiencia ?? null,
          }))
          .sort((a: IItemHistoricoServico, b: IItemHistoricoServico) => {
            const dateA = new Date(a.criado).getTime();
            const dateB = new Date(b.criado).getTime();
            return dateB - dateA;
          });

        setHistorico(agruparHistoricoServico(historicoMapeado));
      }
    } catch (error: any) {
      console.error('Erro ao carregar histórico:', error);
      setErro(true);
      setHistorico([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (abaAtiva === 'historico') {
      carregaHistorico();
    }
  }, [abaAtiva, carregaHistorico]);

  if (abaAtiva !== 'historico') {
    return null;
  }

  return (
    <Container>
      <ContainerBloco>
        <TituloBloco>
          <Clock size={16} color={comum.colors.primaryRed400}/>
          Histórico de Serviços Template
        </TituloBloco>

        <div className="historico">
          {(loading || loadingParent) ? (
            [1, 2, 3, 4, 5].map((index) => (
              <SkeletonItemHistorico key={index} lastItem={index === 5} />
            ))
          ) : erro ? (
            <HistoricoErro onRetry={carregaHistorico} />
          ) : historico.length > 0 ? (
            historico.map((item, index) => (
              <ItemHistorico key={item.id} item={item} lastItem={index === historico.length - 1} />
            ))
          ) : (
            <HistoricoVazio />
          )}
        </div>
      </ContainerBloco>
    </Container>
  );
}
